import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import get from 'lodash/get';
import map from 'lodash/map';
import moment from 'moment-timezone';
import produce from 'immer';
import set from 'lodash/set';

import FormContext from 'src/presentation/contexts/FormContext';
import InputField from 'src/presentation/components/forms/InputField';
import NodeSelector from '../NodeSelector';
import useOptionsGeneratedIds from 'src/presentation/components/pages/ivr/hooks/useOptionsGeneratedIds';
import {
  Content,
  Footer,
  Row,
  ScrollRow,
  ContainerForOptions,
} from 'src/presentation/components/pages/ivr/layouts/IvrLayout';
import { EditSchedule } from 'src/presentation/components/pages/ivr/components/nodes/components/EditSchedule';
import {
  NodeProps,
  SIDE_PANEL_ROW_TYPES,
  DESCRIPTION_PLACEHOLDER_TEXT,
  DESCRIPTION_LABEL_TEXT,
} from 'src/presentation/components/pages/ivr/constants/ivrConstants';
import { OptionContainer } from 'src/presentation/components/pages/ivr/layouts/IvrLayout';
import { ScheduleType } from 'src/models/Ivrs/Nodes/ScheduleNodeDef';

const ScheduleNode: FC<NodeProps> = ({ attr, disabled, nodeList, onSelectNode, readOnly, selectedOptionIndex }) => {
  const { onBlur, overrideState, workingState } = useContext(FormContext);
  const [selectedIndex, setSelectedIndex] = useState(selectedOptionIndex);
  const [showNewItem, setShowNewItem] = useState(false);
  const currentNodeId = get(workingState, `${attr}.id`, '');

  const items = get(workingState, `${attr}.nodeSchedules`, []);
  const { optionsIds, removeOptionIdByIndex } = useOptionsGeneratedIds(items);

  useEffect(() => {
    setSelectedIndex(selectedOptionIndex);
  }, [selectedOptionIndex]);
  const schedules = get(workingState, `${attr}.nodeSchedules`, []);

  const onAddButtonClick = () => {
    setShowNewItem(true);
  };

  const onDelete = useCallback(
    (index, optionsAttr) => {
      const updatedState = produce(workingState, draftState => {
        const items = get(draftState, optionsAttr, []);
        items.splice(index, 1);
        set(draftState, optionsAttr, items);
      });
      overrideState && overrideState(updatedState);
      onBlur && onBlur({ currentState: updatedState });
    },
    [onBlur, overrideState, workingState]
  );

  const onDeleteFromShortPresentation = index => {
    removeOptionIdByIndex(index);
    onDelete(index, `${attr}.nodeSchedules`);
  };

  const handleCancel = () => {
    setShowNewItem(false);
  };

  const renderScheduleOption = ({ idx, schedule }) => {
    const scheduleItemAttr = `${attr}.nodeSchedules[${idx}]`;

    const isNew = !get(workingState, scheduleItemAttr);
    return (
      <OptionContainer
        actionRow={() => null}
        attrList={`${attr}.nodeSchedules`}
        currentIndex={idx}
        data-aid={`nodeSchedules[${idx}]`}
        disabled={disabled}
        key={`${attr}-${optionsIds[idx]}`}
        onDeleteOption={onDelete}
        readOnly={readOnly}
        removeOptionIdByIndex={removeOptionIdByIndex}
        selectedIndex={selectedIndex}
        className={isNew ? 'new-item schedule-option' : 'schedule-option'}
      >
        <EditSchedule
          attr={scheduleItemAttr}
          currentIndex={idx}
          currentSchedule={schedule}
          data-aid={`nodeSchedules[${idx}]`}
          disabled={disabled}
          nodeAttr={attr}
          nodeList={nodeList}
          omittedNodeIds={[currentNodeId]}
          onDelete={onDeleteFromShortPresentation}
          onSelectNode={onSelectNode}
          onSwitchToShortPresentation={handleCancel}
          openInEditMode={idx === schedules.length}
          readOnly={readOnly}
        />
      </OptionContainer>
    );
  };

  const renderedSchedules = () => {
    const context = map(schedules, (schedule, idx: number) => {
      return renderScheduleOption({ idx, schedule });
    });

    if (showNewItem) {
      context.push(
        renderScheduleOption({
          idx: schedules.length,
          schedule: {
            nodeId: '',
            schedule: {
              type: ScheduleType.SCHEDULE_TYPE_DATE,
              schedule: {
                daysOfTheWeek: [],
                location: moment.tz.guess(),
                date: moment().format('MM/DD/YYYY'),
                timeRanges: [{ start: '', end: '' }],
              },
            },
          },
        })
      );
    }

    return (
      <ContainerForOptions
        addButtonLabel="Add Schedule"
        data-aid={`nodeSchedules`}
        disabled={disabled || readOnly}
        label="Schedules"
        onAddButtonClick={onAddButtonClick}
        context={context}
      />
    );
  };

  return (
    <>
      <Content>
        <Row>
          <InputField
            attr={`${attr}.name`}
            data-aid="name"
            description="The name you can refer to link to other nodes"
            disabled={disabled}
            label="Name"
            readOnly={readOnly}
          />
        </Row>

        <Row>
          <InputField
            attr={`${attr}.description`}
            data-aid="description"
            disabled={disabled}
            label={DESCRIPTION_LABEL_TEXT}
            placeholder={DESCRIPTION_PLACEHOLDER_TEXT}
            readOnly={readOnly}
          />
        </Row>

        {renderedSchedules()}
      </Content>
      <Footer>
        <ScrollRow scrollToIndex={selectedOptionIndex} type={SIDE_PANEL_ROW_TYPES.NOT_MATCH_ROW}>
          <NodeSelector
            searchable
            attr={`${attr}.defaultNextId`}
            data-aid="defaultNextId"
            description="The next IVR node if none of the schedules match the current time"
            disabled={disabled}
            label="Default Next Node"
            omittedNodeIds={[currentNodeId]}
            onSelectNode={onSelectNode}
            options={nodeList}
            readOnly={readOnly}
          />
        </ScrollRow>
      </Footer>
    </>
  );
};

export default ScheduleNode;
